import { Link } from 'react-router-dom';
import { Navbar } from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  Shield, 
  FileCheck, 
  Clock, 
  Download, 
  CheckCircle2, 
  ArrowRight,
  Building2,
  Lock,
  Zap,
  Mail
} from 'lucide-react';

const LOGO_URL = "https://kroytechnologygroup.com/wp-content/uploads/2022/01/KTG-Final-Logo-Flat-300x200.png";

const features = [
  {
    icon: Shield,
    title: "Industry Compliant",
    description: "Templates designed to meet industry standards and regulatory requirements."
  },
  {
    icon: Clock,
    title: "Work at Your Pace",
    description: "Save your progress and return anytime. No pressure, no deadlines."
  },
  {
    icon: Lock,
    title: "Secure & Encrypted",
    description: "Your data is protected with enterprise-grade security measures."
  },
  {
    icon: Download,
    title: "Export to PDF",
    description: "Generate professional PDF documents with your branding included."
  }
];

const templates = [
  {
    name: "Business Continuity Plan",
    description: "Ensure your organization can continue operating during disruptions.",
    category: "Business Continuity"
  },
  {
    name: "Disaster Recovery Plan",
    description: "Recover IT infrastructure and systems after a disaster.",
    category: "Disaster Recovery"
  },
  {
    name: "Incident Response Plan",
    description: "Detect, respond to, and recover from security incidents.",
    category: "Security"
  },
  {
    name: "IT Security Policy",
    description: "Comprehensive security policy covering all IT aspects.",
    category: "Security"
  }
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="hero-section text-white py-24 lg:py-32 relative">
        <div className="page-container relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-8">
              <Shield className="h-4 w-4 text-[#FF6B35]" />
              <span className="text-sm font-medium">Compliance Made Simple</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Build Your Compliance Documents with Confidence
            </h1>
            
            <p className="text-xl text-white/80 mb-10 leading-relaxed max-w-2xl">
              Create industry-standard Business Continuity Plans, Disaster Recovery Plans, and Security Policies with our guided templates.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/login">
                <Button 
                  className="bg-[#FF6B35] hover:bg-[#e55a2a] text-white px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                  data-testid="hero-get-started-btn"
                >
                  Client Login
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute right-0 top-0 w-1/3 h-full opacity-10">
          <div className="absolute right-10 top-20 w-64 h-64 border border-white rounded-full"></div>
          <div className="absolute right-32 top-40 w-48 h-48 border border-white rounded-full"></div>
          <div className="absolute right-20 top-60 w-32 h-32 border border-white rounded-full"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50">
        <div className="page-container">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Everything You Need for Continuity
            </h2>
            <p className="text-slate-600 text-lg">
              Our platform guides you through creating comprehensive compliance documents that meet industry standards.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-lg hover:border-[#FF6B35]/30 transition-all duration-200 group"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#FF6B35] transition-colors duration-200">
                  <feature.icon className="h-6 w-6 text-[#FF6B35] group-hover:text-white transition-colors duration-200" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Templates Section */}
      <section className="py-20">
        <div className="page-container">
          <div className="flex flex-col lg:flex-row gap-12 items-start">
            <div className="lg:w-1/3 lg:sticky lg:top-24">
              <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Compliance Templates
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Choose from our collection of industry-standard compliance templates. Each template is designed to help you meet regulatory requirements and protect your business.
              </p>
              <Link to="/login">
                <Button className="btn-primary px-6 py-3" data-testid="templates-cta-btn">
                  Start Building
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="lg:w-2/3 grid sm:grid-cols-2 gap-4">
              {templates.map((template, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-lg hover:border-[#FF6B35]/30 transition-all duration-200"
                  data-testid={`template-preview-${index}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center">
                      <FileCheck className="h-5 w-5 text-[#FF6B35]" />
                    </div>
                    <span className="text-xs font-medium text-[#FF6B35] bg-[#FF6B35]/10 px-2 py-1 rounded-full">
                      {template.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {template.name}
                  </h3>
                  <p className="text-slate-600 text-sm">
                    {template.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="page-container">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How It Works
            </h2>
            <p className="text-slate-400 text-lg">
              Get your compliance documents ready in three simple steps.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Choose Template", description: "Select the compliance template that fits your needs." },
              { step: "02", title: "Fill Information", description: "Complete the guided form at your own pace with auto-save." },
              { step: "03", title: "Download PDF", description: "Export your professional, branded compliance document." }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-slate-400">
                  {item.description}
                </p>
                {index < 2 && (
                  <ArrowRight className="hidden md:block absolute -right-4 top-8 h-8 w-8 text-slate-700" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="page-container">
          <div className="bg-[#FF6B35] rounded-2xl p-12 text-center text-white relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Ready to Build Your Compliance Documents?
              </h2>
              <p className="text-white/80 mb-6 max-w-xl mx-auto">
                Contact us to get your organization set up on the AuditGuard portal.
              </p>
              <Link to="/login?contact=true">
                <Button 
                  className="bg-white text-[#FF6B35] hover:bg-white/90 px-8 py-4 text-lg rounded-lg font-semibold shadow-lg"
                  data-testid="cta-contact-btn"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Contact Us to Get Set Up
                </Button>
              </Link>
              <p className="text-white/70 mt-6 text-sm">
                Or if you already have an account:
              </p>
              <Link to="/login">
                <Button 
                  className="mt-2 bg-transparent border-2 border-white/60 hover:border-white hover:bg-white/10 text-white px-8 py-3 text-base rounded-lg"
                  data-testid="cta-client-login-btn"
                >
                  Client Login
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute -right-20 -top-20 w-96 h-96 border border-white rounded-full"></div>
              <div className="absolute -left-20 -bottom-20 w-64 h-64 border border-white rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="page-container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="AuditGuard" className="h-10 w-auto rounded-lg" />
              <div>
                <span className="font-semibold block" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  AuditGuard
                </span>
                <span className="text-sm text-slate-400">IT Services & Compliance Solutions</span>
              </div>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <a href="https://www.audit-guard.net" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Main Website
              </a>
              <a href="https://www.audit-guard.net/contact" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Contact Us
              </a>
              <span>© {new Date().getFullYear()} AuditGuard by Kroy Technology Group</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
